import { form_values } from "@/redux/vps/qemu/qemu.actions";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import { Field, useFormikContext } from "formik";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { read_country_list } from "../../country-utils";

export default function InputPhoneNumber({
	dial_code_on_change = null,
	digits_on_change = null,
	disabled = false,
	label = null,
	name,
	name_dial_code,
	placeholder = "",
	size = "small",
}) {
	// Hooks
	const [countryList, setCountryList] = useState([]);
	const [dialCodeDuplicate, setDialCodeDuplicate] = useState(false);
	const [dialCodeDuplicateInitChecked, setDialCodeDuplicateInitChecked] = useState(false);
	const { values } = useFormikContext();

	// Variables
	const phoneNumberCountryCode = `${name}_country_code`;

	// Fetches country list
	useEffect(() => {
		const fetchCountries = async () => {
			const country_list = await read_country_list();

			setCountryList(country_list.data);
		};

		fetchCountries();
	}, []);

	// Determ if a dialog code is shared between multiple countries on loading.
	useEffect(() => {
		if (
			!dialCodeDuplicateInitChecked &&
			Object.hasOwn(values, name_dial_code) &&
			countryList.length > 0 &&
			values[name_dial_code]
		) {
			const isDuplicate = determIsDuplicate(countryList, values[name_dial_code]);

			setDialCodeDuplicateInitChecked(true);
			setDialCodeDuplicate(isDuplicate);
		}
	}, [countryList, dialCodeDuplicateInitChecked, values, name_dial_code]);

	return (
		<Field name={name}>
			{({ field, meta, form }) => {
				// Set field to empty when not set
				if (!field.value) {
					field.value = "";
				}

				// Handlers
				const onChangeHandler = (event) => {
					// Handle formik on change
					field.onChange(event);

					// Handle custom on change
					digits_on_change?.(event.currentTarget.value);
				};

				return (
					<>
						<TextField
							{...field}
							onChange={onChangeHandler}
							error={(meta.error && meta.touched) || form.errors[name_dial_code]}
							fullWidth
							label={label}
							name={name}
							placeholder={placeholder || ""}
							disabled={disabled}
							type="text"
							variant="outlined"
							size={size}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<CountrySelect
											dial_code_on_change={dial_code_on_change}
											country_list={countryList}
											disabled={disabled}
											phone_number_country_code={phoneNumberCountryCode}
											name_dial_code={name_dial_code}
											set_dial_code_duplicate={setDialCodeDuplicate}
											form={form}
										/>
									</InputAdornment>
								),
							}}
						/>

						{dialCodeDuplicate && (
							<Box display="flex" alignItems="center">
								<Alert severity="info" variant="filled" sx={{ mt: 1 }}>
									<Trans
										i18nKey="inputs.phone_number.duplicate_dial_code"
										values={{ dial_code: form.values[name_dial_code] }}
									/>
								</Alert>
							</Box>
						)}

						{meta.error && meta.touched && (
							<FormHelperText sx={{ color: "error.main", mb: -0.75, ml: 1, mt: 0.25 }}>
								{meta.error}
							</FormHelperText>
						)}

						{form.errors[name_dial_code] && (
							<FormHelperText sx={{ color: "error.main", mb: -0.75, ml: 1, mt: 0.25 }}>
								{form.errors[name_dial_code]}
							</FormHelperText>
						)}
					</>
				);
			}}
		</Field>
	);
}

function CountrySelect({
	dial_code_on_change = null,
	country_list,
	phone_number_country_code,
	name_dial_code,
	set_dial_code_duplicate,
	disabled,
	form,
}) {
	// Hooks
	const { t } = useTranslation();
	const previousSearchTerm = useRef("");
	const [filteredOptions, setFilteredOptions] = useState([]);

	// Load country code default when no form values
	useEffect(() => {
		if (!form.values[name_dial_code] && country_list.length > 0) {
			const option = readFirstCountryByDialCode(country_list, form_values[name_dial_code]);
			if (option) {
				form.setFieldValue(name_dial_code, option.dial_code);
				form.setFieldValue(phone_number_country_code, option.country_code);
			}
		}
	}, [name_dial_code, country_list, phone_number_country_code, form]);

	if (country_list.length <= 0) {
		return;
	}

	// Debounce delay in ms
	const debounceDelay = 150;

	const createDebounce = (callback, delay = debounceDelay) => {
		let timeoutId;

		return (...args) => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => callback(...args), delay);
		};
	};

	const debouncedFilter = createDebounce((searchTerm, options) => {
		// Only process if there's a search term
		if (searchTerm) {
			const filteredResults = new Map();

			for (const option of options) {
				const key = `${option.country_code}-${option.dial_code}`;
				const matches =
					option.country_code.toLowerCase().includes(searchTerm) ||
					option.label.toLowerCase().includes(searchTerm) ||
					option.dial_code.includes(searchTerm);

				if (matches && !filteredResults.has(key)) {
					filteredResults.set(key, option);
				}
			}

			setFilteredOptions(Array.from(filteredResults.values()));
		}
	}, debounceDelay);

	// Attach I18N country label to support multi-language
	const countryListI18 = country_list
		.map((country) => ({
			...country,
			label: t(`country_list.${country.country_code.toUpperCase()}`),
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	return (
		<Field name={name_dial_code}>
			{/* @ts-ignore */}
			{({ field, form }) => {
				// Get country either from the dial code or from the country code
				const country = form.values[phone_number_country_code]
					? countryObjectReadByCode(country_list, form.values[phone_number_country_code])
					: readFirstCountryByDialCode(
							country_list,
							field.value,
							form.values[phone_number_country_code],
						);

				return (
					<Box display="flex" alignItems="center">
						<Box sx={{ borderRight: "1px solid", mr: 1 }}>
							<Box sx={{}}>
								{/* AUTOCOMPLETE SELECTOR  */}
								<Autocomplete
									disabled={disabled}
									onChange={(_, option) => {
										// Store data about the dial code in the form
										form.setFieldValue(name_dial_code, option.dial_code);
										form.setFieldValue(phone_number_country_code, option.country_code);
										dial_code_on_change?.(option.dial_code);

										// Determ if updated dial code is a duplicate
										const isDuplicate = determIsDuplicate(country_list, option.dial_code);
										set_dial_code_duplicate(isDuplicate);
									}}
									options={filteredOptions.length > 0 ? filteredOptions : countryListI18}
									value={country}
									disableClearable
									autoHighlight
									isOptionEqualToValue={(option, value) =>
										option?.country_code === value?.country_code
									}
									fullWidth
									filterOptions={(_, { inputValue }) => {
										const searchTerm = inputValue.toLowerCase().trim();

										// Only trigger filter if search term has changed
										if (searchTerm && searchTerm !== previousSearchTerm.current) {
											previousSearchTerm.current = searchTerm;
											debouncedFilter(searchTerm, countryListI18);
											return filteredOptions;
										}

										// Return filtered results if we have a search term
										if (searchTerm) {
											return filteredOptions;
										}

										// Return full list when no manual input
										return countryListI18;
									}}
									getOptionLabel={(option) => option.country_code}
									size="small"
									getOptionKey={(option) => `${option.dial_code}_${option.label}`}
									renderOption={(props, option) => (
										<CountryCodeOptionItem {...props} key={props.key} option={option} />
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											variant="standard"
											fullWidth
											InputProps={{
												...params.InputProps,
												disableUnderline: true,
												startAdornment: (
													<CountyCodeImage
														country_code={country?.country_code}
														disabled={disabled}
													/>
												),
												sx: {
													width: "90%",
												},
											}}
											inputProps={{
												...params.inputProps,
												autoComplete: "new-password",
											}}
										/>
									)}
									sx={{
										"& .MuiAutocomplete-inputRoot": {
											fontSize: 15,
											padding: 0,
										},
									}}
									PaperComponent={(props) => <Paper {...props} sx={{ width: 300 }} />}
								/>
							</Box>
						</Box>
						<Box>
							{typeof field.value === "string" ? field.value : field.value?.dial_code || ""}
						</Box>
					</Box>
				);
			}}
		</Field>
	);
}

/* -------------------------------------------------------------------------- */
/*                                 OPTION ITEM                                */
/* -------------------------------------------------------------------------- */
function CountryCodeOptionItem(props) {
	const { option } = props;

	return (
		<Box component="li" {...props}>
			<CountyCodeImage country_code={option.country_code} />

			<Box>
				<Box component="span" fontSize={14} mr={1}>
					{option.label}
				</Box>

				<Chip
					label={<Box sx={{ fontSize: 12, opacity: 0.8 }}>{option.dial_code}</Box>}
					size="small"
					variant="outlined"
				/>
			</Box>
		</Box>
	);
}

/* -------------------------------------------------------------------------- */
/*                                IMAGE SECTION                               */
/* -------------------------------------------------------------------------- */
function CountyCodeImage({ country_code = null, disabled = false }) {
	// Return nothing when no 'country code'
	if (!country_code) {
		return null;
	}

	return (
		<Box
			component="img"
			src={`/img/icons/language/${country_code.toLowerCase()}.svg`}
			alt={country_code}
			sx={{ marginRight: 1, opacity: disabled ? 0.5 : 1, width: 20 }}
		/>
	);
}

const countryObjectReadByCode = (countryList, countryCode) => {
	if (!countryCode) {
		return null;
	}

	return (
		countryList.find(
			(country) => country.country_code.toUpperCase() === countryCode.toUpperCase(),
		) || null
	);
};

const readFirstCountryByDialCode = (countryList, dialCode, countryCode = null) =>
	countryList.find(
		(country) =>
			country.dial_code === dialCode && (!countryCode || country.country_code === countryCode),
	);

const determIsDuplicate = (countryList, dialCode) => {
	let count = 0;
	return countryList.some((country) => {
		if (country.dial_code === dialCode) {
			count++;
			return count > 1;
		}
		return false;
	});
};
